import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { HeartPulse, Leaf, Brain, Apple, Wind, ExternalLink, Star, Headphones, ArrowRight, Loader2 } from "lucide-react";

const SECTION_META = {
  "Ancestral & Herbal Medicine": { icon: Leaf, accent: "text-emerald-300 border-emerald-400/30", blurb: "Plant medicine and healing traditions carried from the continent through the Diaspora." },
  "Mind, Spirit & Emotional Wellness": { icon: Brain, accent: "text-teal-300 border-teal-400/30", blurb: "Culturally-rooted mental health, trauma healing and spiritual care." },
  "Food as Medicine & Nutrition": { icon: Apple, accent: "text-amber-300 border-amber-400/30", blurb: "The African Heritage way of eating — food as the first medicine." },
  "Movement, Breath & Body": { icon: Wind, accent: "text-violet-300 border-violet-400/30", blurb: "Yoga, breath and movement to restore calm and strength in the body." },
};
const ORDER = ["Ancestral & Herbal Medicine", "Mind, Spirit & Emotional Wellness", "Food as Medicine & Nutrition", "Movement, Breath & Body"];

export default function Healing() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/healing").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const grouped = {};
  items.forEach((r) => { (grouped[r.section] = grouped[r.section] || []).push(r); });
  const sections = [...ORDER.filter((s) => grouped[s]), ...Object.keys(grouped).filter((s) => !ORDER.includes(s))];

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="healing-page">
      <div className="relative border-b border-white/10 grain bg-surface overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/40 via-onyx to-onyx" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><HeartPulse size={16} /> Health & Healing</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">Restore the Whole Being</h1>
          <p className="text-bone/60 max-w-2xl text-lg">Healing for the body, mind and spirit — rooted in African and Diaspora traditions of natural medicine, sacred nutrition, movement and emotional wellness. Reclaim the wisdom that kept our ancestors whole.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 space-y-16">
        {loading ? (
          <div className="text-bone/50 flex items-center gap-2 py-16 justify-center"><Loader2 size={18} className="animate-spin" /> Loading resources...</div>
        ) : (
          sections.map((s, si) => {
            const meta = SECTION_META[s] || { icon: HeartPulse, accent: "text-gold border-gold/30", blurb: "" };
            const Icon = meta.icon;
            return (
              <section key={s} className={si > 0 ? "border-t border-white/10 pt-12" : ""} data-testid={`healing-section-${si}`}>
                <div className="mb-8">
                  <p className={`text-xs uppercase tracking-[0.3em] mb-3 flex items-center gap-2 ${meta.accent.split(" ")[0]}`}><Icon size={16} /> Wellness Path</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-bone mb-2">{s}</h2>
                  {meta.blurb && <p className="text-bone/55 max-w-2xl">{meta.blurb}</p>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {grouped[s].map((r) => (
                    <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid="healing-card"
                      className="group block bg-surface border border-white/10 hover:border-gold/50 transition-colors p-6">
                      <div className="flex items-center gap-2 mb-3">
                        {r.featured && <span className="text-gold flex items-center gap-1 text-[10px] uppercase tracking-widest"><Star size={11} /> Featured</span>}
                        <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${meta.accent}`}>{r.source_label || "Resource"}</span>
                      </div>
                      <h3 className="font-serif text-xl text-bone group-hover:text-gold transition-colors leading-snug">{r.title}</h3>
                      <p className="text-bone/55 text-sm mt-2 leading-relaxed">{r.description}</p>
                      <span className="inline-flex items-center gap-1.5 text-gold/80 text-xs mt-4 group-hover:gap-2.5 transition-all">Visit resource <ExternalLink size={12} /></span>
                    </a>
                  ))}
                </div>
              </section>
            );
          })
        )}

        {/* Cross-links */}
        <section className="border-t border-white/10 pt-12 grid md:grid-cols-2 gap-6" data-testid="healing-crosslinks">
          <Link to="/audiobooks" className="group bg-surface border border-white/10 hover:border-gold/50 transition-colors p-8 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-2 flex items-center gap-2"><Headphones size={15} /> Listen</p>
              <h3 className="font-serif text-2xl text-bone group-hover:text-gold transition-colors">Healing Audiobooks</h3>
              <p className="text-bone/50 text-sm mt-1">Consciousness & wellness listens from the library.</p>
            </div>
            <ArrowRight className="text-bone/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
          </Link>
          <Link to="/videos?category=herbal" className="group bg-surface border border-white/10 hover:border-gold/50 transition-colors p-8 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-2 flex items-center gap-2"><Leaf size={15} /> Watch</p>
              <h3 className="font-serif text-2xl text-bone group-hover:text-gold transition-colors">Herbal & Health Videos</h3>
              <p className="text-bone/50 text-sm mt-1">Teachings on natural medicine and the body as temple.</p>
            </div>
            <ArrowRight className="text-bone/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
          </Link>
        </section>
      </div>
    </main>
  );
}
